import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { SignatureCapture } from './SignatureCapture';
import { downloadCompletionPDF, previewCompletionPDF, printCompletionPDF } from './CompletionPDF';
import {
  ArrowLeft,
  Save,
  CheckCircle2,
  User,
  MapPin,
  Phone,
  Mail,
  FileText,
  AlertCircle,
  Loader2,
  Printer,
  Download,
  Eye,
} from 'lucide-react';
import { useSwipeable } from 'react-swipeable';
import { useHotkeys } from 'react-hotkeys-hook';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import './CompletionForm.css';

const completionSchema = z.object({
  customerName: z.string().min(1, 'Customer name is required'),
  customerAddress: z.string().min(1, 'Customer address is required'),
  customerEmail: z.string().email().optional().or(z.literal('')),
  customerPhone: z.string().optional(),
  projectRefNumber: z.string().min(1, 'Project reference is required'),
  installationAddress: z.string().min(1, 'Installation address is required'),
  installationDescription: z.string().min(1, 'Installation description is required'),
  // System Components (consolidated fields)
  panelDetails: z.string().optional(),
  inverter1Details: z.string().optional(),
  inverter2Details: z.string().optional(),
  inverter3Details: z.string().optional(),
  batteryDetails: z.string().optional(),
  // Additional Components
  optimiserDetails: z.string().optional(),
  evChargerDetails: z.string().optional(),
  hotWaterDetails: z.string().optional(),
  atsEpsDetails: z.string().optional(),
  birdGuardDetails: z.string().optional(),
  monitoringDetails: z.string().optional(),
  otherDetails: z.string().optional(),
  // HIES Checklist
  confirmationBuildingRegs: z.boolean(),
  manufacturersManuals: z.boolean(),
  serviceRequirements: z.boolean(),
  maintenanceRequirements: z.boolean(),
  performanceEstimates: z.boolean(),
  mcsCertificate: z.boolean(),
  mcsComplianceConfirmation: z.boolean(),
  // Customer feedback
  customerComments: z.string().optional(),
});

type CompletionFormData = z.infer<typeof completionSchema>;

interface CompletionFormProps {
  record: any;
  onBack: () => void;
  onComplete: () => void;
}

export function CompletionForm({ record, onBack, onComplete }: CompletionFormProps) {
  const [currentSection, setCurrentSection] = useState(0);
  const [signatureImage, setSignatureImage] = useState<string | null>(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>(
    window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
  );
  const [isTablet, setIsTablet] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const sections = [
    { id: 'customer', title: 'Customer Details', icon: User },
    { id: 'installation', title: 'Installation Info', icon: MapPin },
    { id: 'system', title: 'System Components', icon: FileText },
    { id: 'checklist', title: 'HIES Checklist', icon: FileText },
    { id: 'signature', title: 'Signature', icon: CheckCircle2 },
  ];

  // Detect tablet and orientation changes
  useEffect(() => {
    const checkDevice = () => {
      const isTabletDevice = window.innerWidth >= 768 && window.innerWidth <= 1024;
      setIsTablet(isTabletDevice);
    };

    const handleOrientationChange = () => {
      const newOrientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
      setOrientation(newOrientation);
      checkDevice();
    };

    // Initial check
    checkDevice();
    handleOrientationChange();

    // Listen for resize and orientation changes
    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);

    // Also listen for the screen orientation API if available
    if (screen.orientation) {
      screen.orientation.addEventListener('change', handleOrientationChange);
    }

    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
      if (screen.orientation) {
        screen.orientation.removeEventListener('change', handleOrientationChange);
      }
    };
  }, []);

  // Function to load and populate site survey data
  const loadSiteSurveyData = async () => {
    try {
      const response = await fetch(`/api/completion-of-works/${record.id}/site-survey`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.systemComponents) {
          // Update form with formatted system components
          form.setValue('panelDetails', data.systemComponents.panelDetails || '');
          form.setValue('inverter1Details', data.systemComponents.inverter1Details || '');
          form.setValue('inverter2Details', data.systemComponents.inverter2Details || '');
          form.setValue('inverter3Details', data.systemComponents.inverter3Details || '');
          form.setValue('batteryDetails', data.systemComponents.batteryDetails || '');
          form.setValue('optimiserDetails', data.systemComponents.optimiserDetails || '');
          form.setValue('evChargerDetails', data.systemComponents.evChargerDetails || '');
          form.setValue('hotWaterDetails', data.systemComponents.hotWaterDetails || '');
          form.setValue('atsEpsDetails', data.systemComponents.atsEpsDetails || '');
          form.setValue('birdGuardDetails', data.systemComponents.birdGuardDetails || '');
          form.setValue('monitoringDetails', data.systemComponents.monitoringDetails || '');
          form.setValue('otherDetails', data.systemComponents.otherDetails || '');

          // Set a smart default for installation description if it's empty
          if (!form.getValues('installationDescription')) {
            let description = '';
            if (data.systemComponents.panelDetails) {
              description = 'Solar PV System Installation';
            } else if (
              data.systemComponents.batteryDetails &&
              !data.systemComponents.panelDetails
            ) {
              description = 'Battery Storage Installation';
            } else if (data.systemComponents.evChargerDetails) {
              description = 'EV Charger Installation';
            } else {
              description = 'Electrical Installation';
            }
            form.setValue('installationDescription', description);
          }

          toast({
            title: 'Success',
            description: 'Site survey data loaded successfully',
          });
        }
      } else {
        // No site survey data found
      }
    } catch (error) {
      console.error('Error loading site survey:', error);
      toast({
        title: 'Info',
        description: 'No site survey data available',
        variant: 'default',
      });
    }
  };

  const form = useForm<CompletionFormData>({
    resolver: zodResolver(completionSchema),
    defaultValues: {
      customerName: record.customerName || '',
      customerAddress: record.customerAddress || '',
      customerEmail: record.customerEmail || '',
      customerPhone: record.customerPhone || '',
      projectRefNumber: record.projectRefNumber || '',
      installationAddress: record.installationAddress || '',
      installationDescription: record.installationDescription || '',
      // System Components (consolidated)
      panelDetails: record.panelDetails || '',
      inverter1Details: record.inverter1Details || '',
      inverter2Details: record.inverter2Details || '',
      inverter3Details: record.inverter3Details || '',
      batteryDetails: record.batteryDetails || '',
      // Additional Components
      optimiserDetails: record.optimiserDetails || '',
      evChargerDetails: record.evChargerDetails || '',
      hotWaterDetails: record.hotWaterDetails || '',
      atsEpsDetails: record.atsEpsDetails || '',
      birdGuardDetails: record.birdGuardDetails || '',
      monitoringDetails: record.monitoringDetails || '',
      otherDetails: record.otherDetails || '',
      // HIES Checklist
      confirmationBuildingRegs: record.confirmationBuildingRegs || false,
      manufacturersManuals: record.manufacturersManuals || false,
      serviceRequirements: record.serviceRequirements || false,
      maintenanceRequirements: record.maintenanceRequirements || false,
      performanceEstimates: record.performanceEstimates || false,
      mcsCertificate: record.mcsCertificate || false,
      mcsComplianceConfirmation: record.mcsComplianceConfirmation || false,
      customerComments: record.customerComments || '',
    },
  });

  // Auto-save mutation
  const autoSaveMutation = useMutation({
    mutationFn: async (data: CompletionFormData) => {
      const response = await fetch(`/api/completion-of-works/${record.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    },
    onSuccess: () => {
      setLastSaved(new Date());
      setIsAutoSaving(false);
    },
    onError: () => {
      setIsAutoSaving(false);
    },
  });

  // Signature submission mutation
  const signatureMutation = useMutation({
    mutationFn: async (signature: string) => {
      const response = await fetch(`/api/completion-of-works/${record.id}/signature`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          signatureImage: signature,
          signedByName: form.getValues('customerName'),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ 
          error: 'Failed to save signature',
          details: `HTTP error! status: ${response.status}`
        }));
        console.error('Signature save failed:', errorData);
        throw new Error(errorData.error || errorData.details || `HTTP error! status: ${response.status}`);
      }

      return response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'HIES form signed successfully!',
      });
      queryClient.invalidateQueries({ queryKey: ['completions'] });
      onComplete();
    },
    onError: (error: any) => {
      console.error('Signature mutation error:', error);
      const errorMessage = error?.message || 'Failed to save signature. Please try again.';
      toast({
        title: 'Error Saving Signature',
        description: errorMessage,
        variant: 'destructive',
      });
      // Log additional details for debugging
      console.error('Error details:', {
        message: error?.message,
        stack: error?.stack,
        recordId: record.id
      });
    },
  });

  // Auto-load site survey data on mount
  useEffect(() => {
    // Always load site survey data to get the latest information
    loadSiteSurveyData();
  }, [record.id]);

  // Auto-save effect
  useEffect(() => {
    const subscription = form.watch((data) => {
      if (!isAutoSaving) {
        setIsAutoSaving(true);
        const timer = setTimeout(() => {
          autoSaveMutation.mutate(data as CompletionFormData);
        }, 2000); // 2 second debounce
        return () => clearTimeout(timer);
      }
    });
    return () => subscription.unsubscribe();
  }, [form.watch, isAutoSaving]);

  // Function to check if checklist is complete
  const isChecklistComplete = () => {
    const values = form.getValues();
    return (
      values.confirmationBuildingRegs &&
      values.manufacturersManuals &&
      values.serviceRequirements &&
      values.maintenanceRequirements &&
      values.performanceEstimates &&
      values.mcsCertificate &&
      values.mcsComplianceConfirmation
    );
  };

  // Function to handle section navigation (no validation required)
  const handleSectionChange = (newSection: number) => {
    // Simply navigate to the requested section
    setCurrentSection(newSection);
  };

  // Swipe handlers for mobile navigation (disabled on signature section)
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      // Disable swipe when on signature section to prevent conflicts
      if (currentSection === 4) return;
      
      if (currentSection < sections.length - 1) {
        handleSectionChange(currentSection + 1);
      }
    },
    onSwipedRight: () => {
      // Disable swipe when on signature section to prevent conflicts
      if (currentSection === 4) return;
      
      if (currentSection > 0) {
        setCurrentSection(currentSection - 1);
      }
    },
    trackMouse: false,
    preventScrollOnSwipe: true,
    trackTouch: true,
    delta: 50, // Minimum distance for a swipe
  });

  // Keyboard shortcuts
  useHotkeys('ctrl+s, cmd+s', (e) => {
    e.preventDefault();
    form.handleSubmit((data) => autoSaveMutation.mutate(data))();
  });

  useHotkeys('ctrl+left, cmd+left', () => {
    if (currentSection > 0) setCurrentSection(currentSection - 1);
  });

  useHotkeys('ctrl+right, cmd+right', () => {
    if (currentSection < sections.length - 1) handleSectionChange(currentSection + 1);
  });

  const calculateProgress = () => {
    const values = form.getValues();
    const checklistFields = [
      'confirmationBuildingRegs',
      'manufacturersManuals',
      'serviceRequirements',
      'maintenanceRequirements',
      'performanceEstimates',
      'mcsCertificate',
      'mcsComplianceConfirmation',
    ];

    const completedChecklist = checklistFields.filter(
      (field) => values[field as keyof CompletionFormData]
    ).length;
    const hasSignature = !!signatureImage || !!record.signatureImage;

    const totalSteps = checklistFields.length + 1; // checklist + signature
    const completedSteps = completedChecklist + (hasSignature ? 1 : 0);

    return Math.round((completedSteps / totalSteps) * 100);
  };

  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      // First save any changes
      if (record.status !== 'signed') {
        await autoSaveMutation.mutateAsync(form.getValues());
      }

      // Use React PDF for generation
      await downloadCompletionPDF(record, signatureImage || undefined);

      toast({
        title: 'Success',
        description: 'PDF downloaded successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to generate PDF',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handlePreviewPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      // First save any changes
      if (record.status !== 'signed') {
        await autoSaveMutation.mutateAsync(form.getValues());
      }

      // Use React PDF for preview
      await previewCompletionPDF(record, signatureImage || undefined);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to preview PDF',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handlePrintPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      // First save any changes
      if (record.status !== 'signed') {
        await autoSaveMutation.mutateAsync(form.getValues());
      }

      // Use React PDF for printing
      await printCompletionPDF(record, signatureImage || undefined);

      toast({
        title: 'Success',
        description: 'PDF sent to printer',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to print PDF',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleSignatureComplete = async () => {
    // First, validate signature exists
    if (!signatureImage) {
      toast({
        title: 'Signature Required',
        description: 'Please draw your signature in the box above',
        variant: 'destructive',
      });
      return;
    }

    // Show informational message if checklist is incomplete (but don't block)
    if (!isChecklistComplete()) {
      const values = form.getValues();
      const missingItems = [];
      if (!values.confirmationBuildingRegs) missingItems.push('Building regulations confirmation');
      if (!values.manufacturersManuals) missingItems.push("Manufacturers' manuals");
      if (!values.serviceRequirements) missingItems.push('Service requirements');
      if (!values.maintenanceRequirements) missingItems.push('Maintenance requirements');
      if (!values.performanceEstimates) missingItems.push('Performance estimates');
      if (!values.mcsCertificate) missingItems.push('MCS certificate');
      if (!values.mcsComplianceConfirmation) missingItems.push('MCS compliance confirmation');

      // Just show a warning, don't prevent signing
      if (missingItems.length > 0) {
        toast({
          title: 'Note: Checklist Incomplete',
          description: `The following items are not checked: ${missingItems.join(', ')}. Proceeding anyway.`,
          variant: 'default', // Changed from "destructive" to "default" for informational
        });
      }
    }

    // Save any pending changes before signing (non-blocking)
    try {
      await autoSaveMutation.mutateAsync(form.getValues());
    } catch (error) {
      console.warn('Auto-save before signature failed, continuing anyway:', error);
      // Don't block signature submission - just warn
      toast({
        title: 'Warning',
        description: 'Some changes may not have been saved',
        variant: 'default',
      });
    }

    // Submit signature with debugging
    console.log('Submitting signature for record:', record.id);
    console.log('Customer name:', form.getValues('customerName'));
    signatureMutation.mutate(signatureImage);
  };

  return (
    <div
      className={`min-h-screen bg-gray-50 ${
        isTablet && orientation === 'landscape' ? 'tablet-landscape' : ''
      }`}
      {...(currentSection !== 4 ? swipeHandlers : {})}
    >
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={onBack} className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>

            <div className="flex items-center gap-3">
              {isAutoSaving && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Saving...
                </Badge>
              )}
              {lastSaved && !isAutoSaving && (
                <Badge variant="outline" className="text-xs">
                  Saved {lastSaved.toLocaleTimeString()}
                </Badge>
              )}

              {/* PDF Generation Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" disabled={isGeneratingPDF}>
                    {isGeneratingPDF ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Printer className="h-4 w-4 mr-2" />
                        PDF
                      </>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handlePrintPDF}>
                    <Printer className="h-4 w-4 mr-2" />
                    Print to PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDownloadPDF}>
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handlePreviewPDF}>
                    <Eye className="h-4 w-4 mr-2" />
                    Preview PDF
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-3">
            <Progress value={calculateProgress()} className="h-2" />
            <p className="text-xs text-gray-600 mt-1">{calculateProgress()}% Complete</p>
          </div>
        </div>
      </div>

      {/* Section tabs */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <Button
                key={section.id}
                variant={currentSection === index ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleSectionChange(index)}
                className="flex items-center gap-2 whitespace-nowrap"
              >
                <Icon className="h-4 w-4" />
                {section.title}
                {/* Show info icon for signature if checklist incomplete (informational only) */}
                {index === 4 && !isChecklistComplete() && (
                  <AlertCircle className="h-3 w-3 text-yellow-500 ml-1" />
                )}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Form content */}
      <div
        className={`container mx-auto px-4 pb-20 ${
          isTablet && orientation === 'landscape' ? 'landscape-form-container' : ''
        }`}
      >
        <Card
          className={`mx-auto ${
            isTablet && orientation === 'landscape' ? 'max-w-5xl' : 'max-w-4xl'
          }`}
        >
          <CardContent className="p-6">
            {/* Customer Details Section */}
            {currentSection === 0 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold mb-4">Customer Details</h2>

                <div
                  className={`grid gap-4 ${
                    isTablet && orientation === 'landscape' ? 'grid-cols-2' : 'md:grid-cols-2'
                  }`}
                >
                  <div>
                    <Label htmlFor="customerName">Customer Name *</Label>
                    <Input
                      id="customerName"
                      {...form.register('customerName')}
                      className="h-12 text-base"
                    />
                    {form.formState.errors.customerName && (
                      <p className="text-sm text-red-500 mt-1">
                        {form.formState.errors.customerName.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="customerPhone">Phone Number</Label>
                    <Input
                      id="customerPhone"
                      type="tel"
                      {...form.register('customerPhone')}
                      className="h-12 text-base"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="customerEmail">Email Address</Label>
                  <Input
                    id="customerEmail"
                    type="email"
                    {...form.register('customerEmail')}
                    className="h-12 text-base"
                  />
                </div>

                <div>
                  <Label htmlFor="customerAddress">Customer Address *</Label>
                  <Textarea
                    id="customerAddress"
                    {...form.register('customerAddress')}
                    className="min-h-[100px] text-base"
                  />
                  {form.formState.errors.customerAddress && (
                    <p className="text-sm text-red-500 mt-1">
                      {form.formState.errors.customerAddress.message}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Installation Info Section */}
            {currentSection === 1 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold mb-4">Installation Information</h2>

                <div>
                  <Label htmlFor="projectRefNumber">Project Reference *</Label>
                  <Input
                    id="projectRefNumber"
                    {...form.register('projectRefNumber')}
                    className="h-12 text-base"
                  />
                  {form.formState.errors.projectRefNumber && (
                    <p className="text-sm text-red-500 mt-1">
                      {form.formState.errors.projectRefNumber.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="installationAddress">Installation Address *</Label>
                  <Textarea
                    id="installationAddress"
                    {...form.register('installationAddress')}
                    className="min-h-[100px] text-base"
                  />
                  {form.formState.errors.installationAddress && (
                    <p className="text-sm text-red-500 mt-1">
                      {form.formState.errors.installationAddress.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="installationDescription">Installation Description *</Label>
                  <Textarea
                    id="installationDescription"
                    {...form.register('installationDescription')}
                    className="min-h-[100px] text-base"
                    placeholder="e.g., Solar PV System - 4.5kW"
                  />
                  {form.formState.errors.installationDescription && (
                    <p className="text-sm text-red-500 mt-1">
                      {form.formState.errors.installationDescription.message}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* System Components Section */}
            {currentSection === 2 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold mb-4">System Components</h2>

                {/* Main System Components */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="panelDetails">Solar Panels</Label>
                    <Input
                      id="panelDetails"
                      {...form.register('panelDetails')}
                      placeholder="e.g., JA Solar JAM54S30 415W x 10 panels (4.15kWp total)"
                      className="h-12 text-base"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Format: Brand Model Wattage x Quantity (Total kWp)
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="inverter1Details">Inverter 1</Label>
                    <Input
                      id="inverter1Details"
                      {...form.register('inverter1Details')}
                      placeholder="e.g., Solis RHI-3.6K - 3.6kW"
                      className="h-12 text-base"
                    />
                    <p className="text-xs text-gray-500 mt-1">Format: Brand Model - Capacity</p>
                  </div>

                  <div>
                    <Label htmlFor="inverter2Details">Inverter 2 (Optional)</Label>
                    <Input
                      id="inverter2Details"
                      {...form.register('inverter2Details')}
                      placeholder="e.g., Solis RHI-3.6K - 3.6kW"
                      className="h-12 text-base"
                    />
                  </div>

                  <div>
                    <Label htmlFor="inverter3Details">Inverter 3 (Optional)</Label>
                    <Input
                      id="inverter3Details"
                      {...form.register('inverter3Details')}
                      placeholder="e.g., Solis RHI-3.6K - 3.6kW"
                      className="h-12 text-base"
                    />
                  </div>

                  <div>
                    <Label htmlFor="batteryDetails">Battery Storage (Optional)</Label>
                    <Input
                      id="batteryDetails"
                      {...form.register('batteryDetails')}
                      placeholder="e.g., PylonTech US5000 - 4.8kWh"
                      className="h-12 text-base"
                    />
                    <p className="text-xs text-gray-500 mt-1">Format: Brand Model - Capacity</p>
                  </div>
                </div>

                {/* Additional Components */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-3">Additional Components (Optional)</h3>
                  <div className="grid gap-4">
                    <div>
                      <Label htmlFor="optimiserDetails">Optimisers</Label>
                      <Input
                        id="optimiserDetails"
                        {...form.register('optimiserDetails')}
                        placeholder="e.g., Tigo TS4-A-O x 10"
                      />
                    </div>
                    <div>
                      <Label htmlFor="evChargerDetails">EV Charger</Label>
                      <Input
                        id="evChargerDetails"
                        {...form.register('evChargerDetails')}
                        placeholder="e.g., Zappi 7kW Type 2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="hotWaterDetails">Hot Water Circuit</Label>
                      <Input
                        id="hotWaterDetails"
                        {...form.register('hotWaterDetails')}
                        placeholder="e.g., Solar iBoost+"
                      />
                    </div>
                    <div>
                      <Label htmlFor="atsEpsDetails">ATS/EPS</Label>
                      <Input
                        id="atsEpsDetails"
                        {...form.register('atsEpsDetails')}
                        placeholder="e.g., Automatic Transfer Switch"
                      />
                    </div>
                    <div>
                      <Label htmlFor="birdGuardDetails">Bird Guard</Label>
                      <Input
                        id="birdGuardDetails"
                        {...form.register('birdGuardDetails')}
                        placeholder="e.g., 30m Bird Guard Mesh"
                      />
                    </div>
                    <div>
                      <Label htmlFor="monitoringDetails">Monitoring System</Label>
                      <Input
                        id="monitoringDetails"
                        {...form.register('monitoringDetails')}
                        placeholder="e.g., SolarEdge Monitoring Platform"
                      />
                    </div>
                    <div>
                      <Label htmlFor="otherDetails">Other Components</Label>
                      <Textarea
                        id="otherDetails"
                        {...form.register('otherDetails')}
                        placeholder="Any other components installed..."
                        className="min-h-[80px] md:min-h-[100px] touch-manipulation"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* HIES Checklist Section */}
            {currentSection === 3 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold mb-4">HIES Completion Checklist</h2>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 md:p-6 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-blue-800">
                        Please review and check the items that have been completed and provided to
                        the customer.
                      </p>
                      <p className="text-sm text-blue-600 mt-2">
                        ℹ️ These items are recommended but not required for signing.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {[
                    {
                      field: 'confirmationBuildingRegs',
                      label: 'Confirmation of compliance with building regulations',
                    },
                    {
                      field: 'manufacturersManuals',
                      label: "Manufacturers' manuals and warranties",
                    },
                    {
                      field: 'serviceRequirements',
                      label: 'Service requirements and maintenance schedule',
                    },
                    {
                      field: 'maintenanceRequirements',
                      label: 'Maintenance requirements documentation',
                    },
                    { field: 'performanceEstimates', label: 'System performance estimates' },
                    { field: 'mcsCertificate', label: 'MCS certificate provided' },
                    {
                      field: 'mcsComplianceConfirmation',
                      label: 'MCS compliance certificate confirmation',
                    },
                  ].map(({ field, label }) => {
                    const isChecked = form.watch(field as keyof CompletionFormData) as boolean;
                    return (
                      <div
                        key={field}
                        className={`flex items-start space-x-3 p-3 border rounded-lg transition-colors ${
                          !isChecked ? 'border-gray-300 bg-gray-50' : 'border-green-300 bg-green-50'
                        }`}
                      >
                        <Checkbox
                          id={field}
                          checked={isChecked}
                          onCheckedChange={(checked) => {
                            form.setValue(field as keyof CompletionFormData, checked as boolean);
                          }}
                          className="mt-1 h-5 w-5"
                        />
                        <label
                          htmlFor={field}
                          className="text-sm font-medium leading-relaxed cursor-pointer select-none"
                        >
                          {label}
                          {!isChecked && (
                            <span className="text-gray-500 text-xs ml-2">(Optional)</span>
                          )}
                        </label>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6">
                  <Label htmlFor="customerComments">Additional Comments (Optional)</Label>
                  <Textarea
                    id="customerComments"
                    {...form.register('customerComments')}
                    className="min-h-[100px] md:min-h-[120px] text-base md:text-lg touch-manipulation mt-2"
                    placeholder="Any additional notes or comments..."
                  />
                </div>
              </div>
            )}

            {/* Signature Section */}
            {currentSection === 4 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold mb-4">Customer Signature</h2>
                
                {/* Touch navigation disabled notice for mobile/tablet */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 md:hidden">
                  <p className="text-xs text-blue-700">
                    💡 Swipe navigation is disabled on this screen to allow signing. Use the buttons to navigate.
                  </p>
                </div>

                {/* Show informational note if checklist is incomplete */}
                {!isChecklistComplete() && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 md:p-6 mb-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <div>
                        <p className="text-sm text-yellow-800">
                          Note: Some checklist items are not yet completed.
                        </p>
                        <p className="text-sm text-yellow-700 mt-1">
                          You may still proceed with signing, though completing all items is
                          recommended.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentSection(3)}
                          className="mt-3"
                        >
                          <ArrowLeft className="h-4 w-4 mr-2" />
                          Review Checklist
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 md:p-6 mb-4">
                  <p className="text-sm text-blue-800">
                    By signing below, the customer confirms receipt of available documentation and
                    completion of the installation.
                  </p>
                </div>

                {/* Always show signature capture area */}
                <SignatureCapture
                  onSignatureChange={setSignatureImage}
                  existingSignature={record.signatureImage}
                />

                <div className="mt-6 flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setCurrentSection(3)}>
                    Back to Checklist
                  </Button>
                  <Button
                    onClick={handleSignatureComplete}
                    disabled={!signatureImage || signatureMutation.isPending}
                    className="min-w-[120px]"
                  >
                    {signatureMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Complete & Sign
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Mobile navigation buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 md:hidden">
        <div className="flex justify-between gap-3">
          <Button
            variant="outline"
            onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
            disabled={currentSection === 0}
            className="flex-1"
          >
            Previous
          </Button>
          <Button
            onClick={() => handleSectionChange(Math.min(sections.length - 1, currentSection + 1))}
            disabled={currentSection === sections.length - 1}
            className="flex-1"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
